'use client';

import React from 'react';
import { Mail } from 'lucide-react';
import axiosInstance, { endpoints } from '@/lib/axios';
import Button from '@/components/ui/Button';
import { INPUT_CLASS, TEXTAREA_CLASS } from './formStyles';
import useModalDismiss from '@/hooks/useModalDismiss';

interface ShareProgramModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Slug used to target the share-by-mail endpoint. */
    formationSlug?: string;
    /** Formation name shown in the subtitle. */
    programName?: string;
}

/**
 * "Partagez ce programme par mail" modal.
 *
 * Mirrors {@link DownloadProgramModal}'s look (header circle icon, same design
 * tokens, same submit-button states) but captures a share request: sender
 * e-mail ("De"), recipient e-mail ("Votre destinataire"), a free message and a
 * consent checkbox. On submit it POSTs to the formation share endpoint, which
 * e-mails the recipient a link to the program. The parent owns open/close.
 */
const ShareProgramModal: React.FC<ShareProgramModalProps> = ({
    isOpen,
    onClose,
    formationSlug,
    programName,
}) => {
    const [formData, setFormData] = React.useState({
        sender: '',
        recipient: '',
        message: '',
    });
    const [consent, setConsent] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    const titleId = React.useId();

    useModalDismiss(isOpen, onClose);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const isFormValid =
        formData.sender.trim() !== '' &&
        formData.recipient.trim() !== '' &&
        formData.message.trim() !== '' &&
        consent;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid || !formationSlug) return;

        setSubmitting(true);
        setError('');
        try {
            await axiosInstance.post(endpoints.formations.share(formationSlug), {
                sender_email: formData.sender,
                recipient_email: formData.recipient,
                message: formData.message,
                consent,
            });
            setFormData({ sender: '', recipient: '', message: '' });
            setConsent(false);
            onClose();
        } catch {
            setError("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-100050 flex items-center justify-center p-4 bg-[#FFFFFF4D] backdrop-blur-xs"
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full max-w-170 max-h-[90vh] overflow-y-auto bg-white rounded-lg pt-12 pb-6 sm:pb-10 px-5 sm:px-10 border border-[#40BFDE]"
                style={{ boxShadow: "0px 1px 2px 1px #00000014" }}
            >
                {/* Close Button */}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fermer"
                    className="absolute top-4 right-4 inline-flex size-9 items-center justify-center rounded-full text-[#1B365E] hover:text-[#40BFDE] transition-colors cursor-pointer duration-75"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23.2897 4.0987C24.2268 3.16164 24.2268 1.63986 23.2897 0.702796C22.3526 -0.234265 20.8309 -0.234265 19.8938 0.702796L12 8.60409L4.0987 0.710292C3.16164 -0.226769 1.63986 -0.226769 0.702796 0.710292C-0.234265 1.64735 -0.234265 3.16914 0.702796 4.1062L8.60409 12L0.710292 19.9013C-0.226769 20.8384 -0.226769 22.3601 0.710292 23.2972C1.64735 24.2343 3.16914 24.2343 4.1062 23.2972L12 15.3959L19.9013 23.2897C20.8384 24.2268 22.3601 24.2268 23.2972 23.2897C24.2343 22.3526 24.2343 20.8309 23.2972 19.8938L15.3959 12L23.2897 4.0987Z" fill="currentColor" />
                    </svg>
                </button>

                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4">
                        <div className="size-14 sm:size-16 shrink-0 rounded-full bg-[#C7F8FF] flex items-center justify-center">
                            <Mail className="size-7 text-[#40BFDE]" aria-hidden="true" />
                        </div>
                        <h2 id={titleId} className="font-red-hat font-light text-[24px] sm:text-[32px] leading-8 sm:leading-10 text-[#1B365E] tracking-[0.9px]">
                            Partagez ce programme par mail
                        </h2>
                    </div>
                    <p className="text-center font-bold text-[16px] sm:text-[18px] leading-6 sm:leading-7 text-[#1B365E] tracking-[0.4px] max-w-md mx-auto">
                        Envoyez le programme de {programName ?? 'cette formation'} à la personne de votre choix
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 max-w-103 mx-auto">
                    <input
                        type="email"
                        name="sender"
                        aria-label="Votre adresse e-mail"
                        placeholder="De"
                        autoComplete="email"
                        value={formData.sender}
                        onChange={handleChange}
                        className={INPUT_CLASS}
                    />
                    <input
                        type="email"
                        name="recipient"
                        aria-label="Adresse e-mail du destinataire"
                        placeholder="Votre destinataire"
                        value={formData.recipient}
                        onChange={handleChange}
                        className={INPUT_CLASS}
                    />
                    <textarea
                        name="message"
                        aria-label="Votre message"
                        placeholder="Votre message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className={TEXTAREA_CLASS}
                    />

                    <label className="flex items-start gap-2.5 text-[12px] text-[#1B365E] font-medium leading-5 tracking-[0.25px] cursor-pointer">
                        <input
                            type="checkbox"
                            name="consent"
                            checked={consent}
                            onChange={e => setConsent(e.target.checked)}
                            className="mt-0.5 size-4 shrink-0 accent-[#40BFDE] cursor-pointer"
                        />
                        <span>
                            En cochant cette case, j&apos;accepte que les informations saisies soient exploitées dans le cadre de ma demande.
                        </span>
                    </label>

                    {error && <p role="alert" className="text-[12px] text-red-600">{error}</p>}

                    <Button
                        type="submit"
                        variant="primary"
                        size="unset"
                        disabled={!isFormValid || submitting}
                        className="w-full h-12 sm:h-14.5 rounded-lg text-[16px]"
                    >
                        {submitting ? "Envoi..." : "Envoyer"}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default ShareProgramModal;
